import { Box, Button, Flex, IconButton, Menu, MenuButton, MenuDivider, MenuItem, MenuItemOption, MenuList, MenuOptionGroup, Popover, PopoverAnchor, PopoverBody, PopoverContent, Portal, Switch, Text, Textarea, Tooltip, useColorModeValue, VStack, Wrap, WrapItem } from '@chakra-ui/react'
import { Check, ChevronDown, ChevronLeft, Info, Library, ListFilter, Paperclip, Plus, Search, Send, Settings, Square, SquareCheck } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAgents } from '../hooks/agents/useAgents'
import { useCommands } from '../hooks/commands/useCommands'
import { useVisibleKnowledgeSites } from '../hooks/knowledge/useVisibleKnowledgeSites'
import { AIModel } from '../types/modelTypes'
import { CommandDescriptor, CommandOption, ParsedCommand } from '../types/commandTypes'
import { useDocumentUpload } from '../hooks/utility/useDocumentUpload'
import { useFileDragAndDrop } from '../hooks/utility/useFileDragAndDrop'
import { Feature } from '../core/client/features.gen'
import { useFeatures } from '../core/features/FeaturesProvider'
import { UploadedAsset } from '../types/chatTypes'
import { isTypingCommandToken, mergeCommands, parseSlashCommands, unionCommands } from '../core/parsers/slashCommandParser'
import { buildSelectionCommand, describeCommand, describeCommandArgs, isMultiSelectCommand, orderMultiSelectOptions, toggleSelection } from '../core/parsers/commandSelection'
import { nextHighlightIndex } from '../core/parsers/popupNavigation'
import { DocumentAttachment } from './DocumentAttachment/DocumentAttachment'
import { FileDropOverlay } from './FileDropOverlay'
import { FilterOptionLabel } from './selectors/FilterOptionLabel'
import { ScopeBadge } from './AppliedCommandTags'

interface QuestionInputProps {
  onSend: (message: string, attachedDocument?: UploadedAsset, commands?: ParsedCommand[]) => void
  onTyping?: () => void
  onStopTyping?: () => void
  disabled?: boolean
  placeholder?: string
  clearOnSend?: boolean
  hasDocumentsInConversation?: boolean
  models?: AIModel[]
  selectedModelId?: string
  onSelectModel?: (modelId: string | undefined) => void
  // True while a response is streaming: the send button becomes a Stop button.
  isStreaming?: boolean
  // Aborts the in-flight stream (closes the EventSource client-side). Backend
  // cancellation is tracked separately in DVG-222.
  onStop?: () => void
  // Identifies the active conversation. Sticky run-directives (the HR-knowledge
  // toggle) are remembered across turns within one conversation; when this
  // changes (chat switch / new chat) the sticky state is reset to default.
  conversationId?: string
}

export const QuestionInput: React.FC<QuestionInputProps> = ({
  onSend,
  onTyping,
  onStopTyping,
  disabled,
  placeholder,
  clearOnSend,
  hasDocumentsInConversation = false,
  models,
  selectedModelId,
  onSelectModel,
  isStreaming,
  conversationId,
}) => {
  const [question, setQuestion] = useState<string>('')
  // Commands applied via the popup (chips). Typed `/token` shorthand is parsed on send.
  const [appliedCommands, setAppliedCommands] = useState<ParsedCommand[]>([])
  // When the user is drilling into a command's options, this holds that command.
  const [activeCommand, setActiveCommand] = useState<CommandDescriptor | null>(null)
  // Keyboard highlight over whichever list is currently visible. Reset to 0
  // whenever that list changes (filter text, drill in/out, popup open/close).
  const [highlightedIndex, setHighlightedIndex] = useState<number>(0)
  // Multi-select picks live here (lifted out of CommandMultiSelectList) so Tab
  // can toggle the highlighted entity, mirroring a row click.
  const [multiSelected, setMultiSelected] = useState<string[]>([])
  // Search filter over the multi-select options, fed by keystrokes intercepted
  // while the panel is open (they never reach the textarea).
  const [optionFilter, setOptionFilter] = useState<string>('')
  // Set when a pick was just toggled via Tab: the next typed character starts
  // a fresh filter instead of appending, so each search begins empty.
  const filterResetPendingRef = useRef<boolean>(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const { currentAgent } = useAgents()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { isEnabled } = useFeatures()

  // A leading slash opens the command popup while the command token is still
  // being typed; once a space follows it (`/org hello`) the user is writing
  // the message, the popup closes and Enter sends. The registry is fetched
  // lazily on first popup open, but also eagerly when a visual surface needs
  // it up front (knowledge-enabled clients).
  const showCommandPopup = isTypingCommandToken(question)
  const internalSearchEnabled = isEnabled(Feature.INTERNAL_SEARCH)
  const { commands: commandDescriptors } = useCommands(showCommandPopup || internalSearchEnabled)

  // Filter control: the `/org` command's per-user options, surfaced as a
  // checkbox group in the + menu. A single available option makes the filter
  // meaningless, so only surface it when there's a real choice.
  const orgCommand = useMemo(
    () => commandDescriptors.find(command => command.name === 'org'),
    [commandDescriptors],
  )
  const filterOptions = orgCommand?.options ?? []
  const showFilter = !!orgCommand && filterOptions.length > 1
  const filterSelection = useMemo(
    () => appliedCommands.find(command => command.name === 'org')?.args ?? [],
    [appliedCommands],
  )
  const handleFilterSelectionChange = useCallback((selectedValues: string[]) => {
    setAppliedCommands(prev => {
      const withoutFilter = prev.filter(command => command.name !== 'org')
      const command = buildSelectionCommand('org', selectedValues)
      return command ? mergeCommands(withoutFilter, [command]) : withoutFilter
    })
  }, [])

  // Per-site knowledge toggles: each toggleable site has its own on/off
  // command `{name: 'knowledge', args: [sourceKey, 'off']}`. Knowledge search
  // is ON by default for every site; toggling off adds the directive, toggling
  // on removes it.
  const knowledgeToggleCommand = useMemo(
    () => commandDescriptors.find(command => command.name === 'knowledge'),
    [commandDescriptors],
  )
  const { sites: toggleableKnowledgeSites } = useVisibleKnowledgeSites(internalSearchEnabled)

  const disabledKnowledgeSources = useMemo(
    () =>
      new Set(
        appliedCommands
          .filter(cmd => cmd.name === 'knowledge' && cmd.args.length === 2 && cmd.args[1] === 'off')
          .map(cmd => cmd.args[0]),
      ),
    [appliedCommands],
  )

  const toggleKnowledgeSite = useCallback(
    (sourceKey: string) => {
      setAppliedCommands(prev => {
        const without = prev.filter(cmd => !(cmd.name === 'knowledge' && cmd.args[0] === sourceKey))
        if (disabledKnowledgeSources.has(sourceKey)) {
          return without
        }
        return mergeCommands(without, [{ name: 'knowledge', args: [sourceKey, 'off'] }])
      })
    },
    [disabledKnowledgeSources],
  )

  // The HR-knowledge directive is sticky within a conversation (it survives
  // sends, see handleSend), so switching to another chat or starting a new one
  // must drop it back to the default. Keyed on the conversation id, which
  // changes exactly on those transitions.
  useEffect(() => {
    setAppliedCommands([])
  }, [conversationId])

  // Commands offered in the slash popup. `org` also has a dedicated + menu
  // control but is listed here too so typing `/org` reaches the same picker
  // (both write the same applied state). A legacy `display: dropdown` config
  // still hides a command from the popup. `knowledge` is excluded: it's a
  // per-source toggle needing `[sourceKey, on|off]`, which the generic popup
  // can't assemble (it would submit only `on`/`off`) — the + menu switches are
  // its sole entry point.
  const slashCommands = useMemo(
    () =>
      commandDescriptors.filter(
        command => command.display !== 'dropdown' && command.name !== 'knowledge',
      ),
    [commandDescriptors],
  )

  // Every applied directive is surfaced as a removable chip above the input so
  // the user can see what's in effect and reset it. Knowledge is the exception:
  // it has a dedicated "off" hint chip (it's a boolean toggle, not a value).
  const chipCommands = useMemo(
    () => appliedCommands.filter(command => command.name !== 'knowledge'),
    [appliedCommands],
  )

  // Render a chip with human-friendly argument labels: an `org` chip should read
  // "org: KBC, SLOB", not the raw access_scope tokens.
  const describeChip = useCallback(
    (command: ParsedCommand): string => describeCommand(command, commandDescriptors),
    [commandDescriptors],
  )

  const {
    uploadedDocument,
    isUploading,
    isDeleting,
    handleFileUpload,
    handleDocumentDelete,
    clearDocument,
  } = useDocumentUpload()

  const documentUploadEnabled = isEnabled(Feature.DOCUMENT_UPLOAD)

  const { isDraggingFile, isDraggingUnsupportedFile } = useFileDragAndDrop({
    enabled: documentUploadEnabled && !disabled,
    onDropFile: handleFileUpload,
  })

  // Color mode values
  const inputBg = useColorModeValue('white', 'gray.700')
  const borderColor = useColorModeValue('gray.300', 'gray.600')
  const placeholderColor = useColorModeValue('gray.500', 'gray.400')
  const textColor = useColorModeValue('gray.800', 'white')
  const mutedTextColor = useColorModeValue('gray.500', 'gray.400')
  const popoverBg = useColorModeValue('white', 'gray.700')

  const MAX_QUESTION_LENGTH = 30000

  // The token typed after the leading slash, used to filter the command list.
  const commandFilter = useMemo(() => {
    if (!showCommandPopup) return ''
    return question.slice(1).split(/\s/)[0].toLowerCase()
  }, [question, showCommandPopup])

  const filteredCommands = useMemo(() => {
    if (!commandFilter) return slashCommands
    return slashCommands.filter(command => {
      const haystack = [command.name, ...command.aliases].map(value => value.toLowerCase())
      return haystack.some(value => value.startsWith(commandFilter))
    })
  }, [slashCommands, commandFilter])

  // Whether the active command's panel is the checkbox multi-select.
  const isMultiSelect = activeCommand !== null && isMultiSelectCommand(activeCommand)

  // Options shown in the multi-select panel: selected picks pinned on top
  // (always visible, exempt from the filter), then the unselected options
  // matching the typed filter.
  const visibleOptions = useMemo<CommandOption[]>(() => {
    if (!activeCommand || !isMultiSelect) return []
    return orderMultiSelectOptions(activeCommand.options, multiSelected, optionFilter)
  }, [activeCommand, isMultiSelect, multiSelected, optionFilter])

  // Auto-resize textarea based on content
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
    }
  }, [question])

  useEffect(() => {
    if (currentAgent && textareaRef.current) {
      textareaRef.current.focus()
    }
  }, [currentAgent])

  // Reset the drill-down when the popup closes.
  useEffect(() => {
    if (!showCommandPopup) {
      setActiveCommand(null)
    }
  }, [showCommandPopup])

  // Handle typing events for real-time typing indicators
  useEffect(() => {
    if (question && onTyping) {
      onTyping()
    } else if (!question && onStopTyping) {
      onStopTyping()
    }
  }, [question, onTyping, onStopTyping])

  // Strip the leading `/token` (the part being typed into the popup) from the input.
  const clearLeadingSlashToken = useCallback(() => {
    setQuestion(prev => prev.replace(/^\/\S*\s*/, ''))
  }, [])

  // Close the drill-down and reset the input back to a typeable message.
  // Also the multi-select abort path (Space with no picks): picks and filter
  // are discarded.
  const finishCommandSelection = useCallback(() => {
    setActiveCommand(null)
    setMultiSelected([])
    setOptionFilter('')
    filterResetPendingRef.current = false
    clearLeadingSlashToken()
    textareaRef.current?.focus()
  }, [clearLeadingSlashToken])

  // Apply a command that takes no arguments directly as a chip. (`knowledge`
  // never reaches here — it's excluded from the popup and driven by the + menu
  // toggles, which build the full `[sourceKey, on|off]` directive.)
  const applyCommand = useCallback(
    (descriptor: CommandDescriptor) => {
      setAppliedCommands(prev => mergeCommands(prev, [{ name: descriptor.name, args: [] }]))
      finishCommandSelection()
    },
    [finishCommandSelection],
  )

  // Apply a specific option of an arg-taking command as a chip (single-select).
  const applyCommandOption = useCallback(
    (descriptor: CommandDescriptor, optionValue: string) => {
      setAppliedCommands(prev => mergeCommands(prev, [{ name: descriptor.name, args: [optionValue] }]))
      finishCommandSelection()
    },
    [finishCommandSelection],
  )

  // Apply a multi-select as a single union command carrying every picked value.
  // Replaces (not unions) any existing chip for the command, so unchecking an
  // entity in a re-opened panel actually removes it.
  const applyCommandSelection = useCallback(
    (descriptor: CommandDescriptor, selectedValues: string[]) => {
      const command = buildSelectionCommand(descriptor.name, selectedValues)
      setAppliedCommands(prev => {
        const withoutCommand = prev.filter(applied => applied.name !== descriptor.name)
        return command ? mergeCommands(withoutCommand, [command]) : withoutCommand
      })
      finishCommandSelection()
    },
    [finishCommandSelection],
  )

  // Choosing a command in the list: drill into options when it has any, else
  // apply it. A multi-select panel starts from the command's current chip so
  // re-opening it edits the existing selection instead of starting blank.
  const handleCommandSelect = useCallback(
    (descriptor: CommandDescriptor) => {
      if (descriptor.options.length > 0) {
        setActiveCommand(descriptor)
        setOptionFilter('')
        filterResetPendingRef.current = false
        if (isMultiSelectCommand(descriptor)) {
          const applied = appliedCommands.find(command => command.name === descriptor.name)
          setMultiSelected(applied ? [...applied.args] : [])
        }
      } else {
        applyCommand(descriptor)
      }
    },
    [applyCommand, appliedCommands],
  )

  // Leave the option panel and return to the primary command list.
  const handleBackToCommands = useCallback(() => {
    setActiveCommand(null)
    setMultiSelected([])
    setOptionFilter('')
    filterResetPendingRef.current = false
  }, [])

  // Toggle one entity in the multi-select (row click).
  const toggleMultiSelectOption = useCallback((optionValue: string) => {
    setMultiSelected(prev => toggleSelection(prev, optionValue))
  }, [])

  // Tab in the multi-select: toggle the highlighted option, then advance the
  // highlight to the next visible row. The toggled pick jumps into the pinned
  // block on re-render, so "next" is resolved by value against the reordered
  // list rather than by index. The next typed character starts a fresh filter.
  const toggleHighlightedAndAdvance = useCallback(() => {
    if (!activeCommand) return
    const option = visibleOptions[highlightedIndex]
    if (!option) return
    const nextValue = visibleOptions[(highlightedIndex + 1) % visibleOptions.length].value
    const newSelected = toggleSelection(multiSelected, option.value)
    setMultiSelected(newSelected)
    filterResetPendingRef.current = true
    const reordered = orderMultiSelectOptions(activeCommand.options, newSelected, optionFilter)
    const nextIndex = reordered.findIndex(o => o.value === nextValue)
    setHighlightedIndex(nextIndex >= 0 ? nextIndex : 0)
  }, [activeCommand, visibleOptions, highlightedIndex, multiSelected, optionFilter])

  // Confirm the multi-select into a single union command (Confirm button).
  const confirmMultiSelect = useCallback(() => {
    if (activeCommand) {
      applyCommandSelection(activeCommand, multiSelected)
    }
  }, [activeCommand, applyCommandSelection, multiSelected])

  // Enter in the multi-select: hot-accept the highlighted option into the
  // selection and confirm in one stroke. Never deselects — toggling is Tab's
  // job — so Enter on an already-picked row just confirms.
  const confirmWithHighlighted = useCallback(() => {
    if (!activeCommand) return
    const highlighted = visibleOptions[highlightedIndex]
    const selectedValues =
      highlighted && !multiSelected.includes(highlighted.value)
        ? [...multiSelected, highlighted.value]
        : multiSelected
    if (selectedValues.length === 0) return
    applyCommandSelection(activeCommand, selectedValues)
  }, [activeCommand, visibleOptions, highlightedIndex, multiSelected, applyCommandSelection])

  // The rows the highlight navigates over, in render order. For the
  // single-select option panel the first row is the "back" affordance, so the
  // command's options follow it. Keeping this in sync with what the popup
  // renders lets nextHighlightIndex wrap correctly and Tab/Enter pick the right
  // row. Each entry carries the action to run when accepted via keyboard.
  // The multi-select panel navigates over `visibleOptions` instead and has its
  // own key handling (Tab toggles + advances, Enter confirms, Space aborts).
  const navigationItems = useMemo<{ accept: () => void }[]>(() => {
    if (!activeCommand) {
      return filteredCommands.map(command => ({ accept: () => handleCommandSelect(command) }))
    }
    if (isMultiSelect) {
      return []
    }
    const back = { accept: handleBackToCommands }
    const options = activeCommand.options.map(option => ({
      accept: () => applyCommandOption(activeCommand, option.value),
    }))
    return [back, ...options]
  }, [
    activeCommand,
    isMultiSelect,
    filteredCommands,
    handleCommandSelect,
    handleBackToCommands,
    applyCommandOption,
  ])

  // Reset the highlight to the top whenever the visible list changes.
  useEffect(() => {
    setHighlightedIndex(0)
  }, [commandFilter, activeCommand, showCommandPopup])

  // When the multi-select filter changes, land the highlight on the first
  // unselected match (the pinned picks sit above it); fall back to the top.
  // Deliberately keyed on the filter only: toggling manages its own highlight
  // (Tab advances it), and a selection change must not yank it back up.
  useEffect(() => {
    if (!isMultiSelect) return
    const firstUnselected = visibleOptions.findIndex(option => !multiSelected.includes(option.value))
    setHighlightedIndex(firstUnselected >= 0 ? firstUnselected : 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionFilter])

  const removeCommand = useCallback((name: string) => {
    setAppliedCommands(prev => prev.filter(command => command.name !== name))
  }, [])

  const handleSend = useCallback(() => {
    if (!question.trim() || disabled) return

    // Parse any typed `/token` shorthand and strip it from the outgoing message,
    // then union with the commands the user applied via the popup (chips) — a
    // typed `/slob` next to an org chip adds to the scope, it doesn't replace it.
    const { commands: typedCommands, strippedMessage } = parseSlashCommands(question, commandDescriptors)
    // Drop a bare typed `/knowledge` / `/hr`: it's a per-source toggle the
    // backend only accepts as `[sourceKey, on|off]`, and the typed path can't
    // supply a source. Legitimate knowledge toggles come from the + menu with
    // both args already set.
    const commands = unionCommands(appliedCommands, typedCommands).filter(
      command => command.name !== 'knowledge' || command.args.length === 2,
    )

    if (!strippedMessage) return

    onSend(strippedMessage, uploadedDocument, commands.length > 0 ? commands : undefined)

    if (clearOnSend) {
      clearDocument()
      setQuestion('')
      // Every directive is sticky within the conversation: persist the full set
      // that was just sent (including typed `/token` shorthand promoted to a
      // chip), so it rides along on the next turn until the user clears it.
      setAppliedCommands(commands)
    }
  }, [disabled, question, commandDescriptors, appliedCommands, onSend, uploadedDocument, clearDocument, clearOnSend])

  const onQuestionChange = useCallback(
    (ev: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = ev.target.value
      if (!newValue) {
        setQuestion('')
      } else if (newValue.length <= MAX_QUESTION_LENGTH) {
        setQuestion(newValue)
      }
    },
    [],
  )

  const selectedModel = models?.find(m => m.id === selectedModelId)
  const selectedModelName = selectedModel?.display_name || selectedModel?.name
  const showModelPicker = models && models.length > 0 && onSelectModel
  // The + menu only earns its place when it has at least one control to offer.
  const showPlusMenu = documentUploadEnabled || !!knowledgeToggleCommand || showFilter
  const handleAttachClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleFileChange = useCallback(
    async (ev: React.ChangeEvent<HTMLInputElement>) => {
      const file = ev.target.files?.[0]
      if (file) {
        await handleFileUpload(file)
      }
      // Reset input so the same file can be re-selected
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    },
    [handleFileUpload],
  )

  const sendQuestionDisabled = disabled || !question.trim()

  // The popup is meaningfully open only when there are commands to show.
  const isPopupOpen = showCommandPopup && (activeCommand !== null || filteredCommands.length > 0)

  // Keyboard handling on the textarea. When the popup is open, arrows move the
  // highlight and Tab/Enter accept the highlighted row (drilling into a
  // command's options). The multi-select panel has its own keymap: typed
  // characters build a filter over the options (they never reach the
  // textarea), Tab toggles the highlighted pick and advances, Enter
  // hot-accepts the highlighted row and confirms, Space confirms the picks
  // (or aborts when there are none), Backspace edits the filter. When the
  // popup is closed, Enter sends as before.
  const onKeyDown = useCallback(
    (ev: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (ev.nativeEvent.isComposing) {
        return
      }

      if (isPopupOpen && isMultiSelect) {
        if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
          ev.preventDefault()
          const direction = ev.key === 'ArrowDown' ? 1 : -1
          setHighlightedIndex(prev => nextHighlightIndex(prev, visibleOptions.length, direction))
          return
        }
        if (ev.key === 'Tab') {
          ev.preventDefault()
          toggleHighlightedAndAdvance()
          return
        }
        if (ev.key === 'Enter' && !ev.shiftKey) {
          ev.preventDefault()
          confirmWithHighlighted()
          return
        }
        if (ev.key === ' ') {
          // With picks: confirm them as-is (the highlighted row is NOT
          // hot-accepted — that's Enter's job). Without picks: abort, back
          // to typing the message.
          ev.preventDefault()
          if (multiSelected.length > 0) {
            confirmMultiSelect()
          } else {
            finishCommandSelection()
          }
          return
        }
        if (ev.key === 'Backspace' && optionFilter) {
          ev.preventDefault()
          setOptionFilter(prev => prev.slice(0, -1))
          return
        }
        if (ev.key.length === 1 && !ev.ctrlKey && !ev.metaKey && !ev.altKey) {
          ev.preventDefault()
          const startFresh = filterResetPendingRef.current
          filterResetPendingRef.current = false
          setOptionFilter(prev => (startFresh ? ev.key : prev + ev.key))
          return
        }
        return
      }

      if (isPopupOpen) {
        if (ev.key === 'ArrowDown') {
          ev.preventDefault()
          setHighlightedIndex(prev => nextHighlightIndex(prev, navigationItems.length, 1))
          return
        }
        if (ev.key === 'ArrowUp') {
          ev.preventDefault()
          setHighlightedIndex(prev => nextHighlightIndex(prev, navigationItems.length, -1))
          return
        }
        if ((ev.key === 'Tab' || ev.key === 'Enter') && !ev.shiftKey) {
          ev.preventDefault()
          navigationItems[highlightedIndex]?.accept()
          return
        }
      }

      // Popup closed (or a key the popup ignores): Enter sends the message.
      if (ev.key === 'Enter' && !ev.shiftKey) {
        ev.preventDefault()
        handleSend()
      }
    },
    [
      isPopupOpen,
      isMultiSelect,
      visibleOptions,
      toggleHighlightedAndAdvance,
      confirmWithHighlighted,
      confirmMultiSelect,
      multiSelected,
      finishCommandSelection,
      optionFilter,
      navigationItems,
      highlightedIndex,
      handleSend,
    ],
  )

  return (
    <VStack spacing={2} width='100%'>
      {(isDraggingFile || isUploading) && (
        <Portal>
          <FileDropOverlay
            label={t('chat.dropFilesHere')}
            isUnsupportedFileType={isDraggingUnsupportedFile}
            unsupportedLabel={t('chat.unsupportedFileType')}
            isUploading={isUploading}
            uploadingLabel={t('chat.uploadingFile')}
            hintLabel={t('chat.uploadLimit')}
          />
        </Portal>
      )}

      {(chipCommands.length > 0 || disabledKnowledgeSources.size > 0) && (
        <Wrap width='100%' spacing={2}>
          {toggleableKnowledgeSites
            .filter(site => disabledKnowledgeSources.has(site.key))
            .map(site => (
              <WrapItem key={site.key}>
                <ScopeBadge
                  icon={Library}
                  label={t('chat.knowledgeSourceOff', { name: site.name })}
                  size='md'
                  onRemove={() => toggleKnowledgeSite(site.key)}
                />
              </WrapItem>
            ))}
          {chipCommands.map(command => {
            const isFilter = command.name === 'org'
            return (
              <WrapItem key={command.name}>
                <ScopeBadge
                  icon={isFilter ? ListFilter : undefined}
                  label={
                    isFilter
                      ? `${t('chat.filteredTo')}: ${describeCommandArgs(command, commandDescriptors)}`
                      : describeChip(command)
                  }
                  size='md'
                  onRemove={() => removeCommand(command.name)}
                />
              </WrapItem>
            )
          })}
        </Wrap>
      )}

      <Popover
        isOpen={isPopupOpen}
        placement='top-start'
        autoFocus={false}
        closeOnBlur={false}
        matchWidth
      >
        <PopoverAnchor>
          <Box
            borderWidth='1px'
            borderRadius='xl'
            bg={inputBg}
            borderColor={borderColor}
            position='relative'
            boxShadow='lg'
            p='2px'
            _hover={{
              boxShadow: 'xl',
              borderColor: 'brand.500',
            }}
            transition='all 0.2s'
            width='100%'
          >
            {/* Document attachment preview above textarea */}
            {uploadedDocument && (
              <Box px={2} pt={2}>
                <DocumentAttachment
                  document={uploadedDocument}
                  onRemove={handleDocumentDelete}
                  isRemoving={isDeleting}
                  variant='input'
                />
              </Box>
            )}

            <Flex position='relative'>
              <Textarea
                ref={textareaRef}
                variant='unstyled'
                placeholder={placeholder || t('chat.placeholder')}
                value={question}
                onChange={onQuestionChange}
                onKeyDown={onKeyDown}
                onFocus={onTyping}
                onBlur={onStopTyping}
                isDisabled={disabled}
                tabIndex={currentAgent ? 0 : -1}
                autoFocus={true}
                px={4}
                py={0}
                pt={4}
                pb={0}
                pr={4}
                // 16px is the iOS Safari threshold below which focusing a field
                // auto-zooms the page (and never zooms back out completely); stay at 16 to
                // suppress that without disabling pinch-zoom via the viewport meta.
                fontSize='16px'
                bg={inputBg}
                color={textColor}
                minH='unset'
                height='23px'
                maxH='336px'
                resize='none'
                overflow='auto'
                lineHeight='1.4'
                _placeholder={{
                  color: placeholderColor,
                }}
              />
            </Flex>

            {/* Bottom toolbar: + menu (left); model picker + send/stop (right) */}
            <Flex px={3} py={1.5} alignItems='center' justifyContent='space-between' gap={2}>
              {showPlusMenu ? (
                <Menu placement='top-start' closeOnSelect={false}>
                  <MenuButton
                    as={IconButton}
                    aria-label={t('chat.addMenu')}
                    icon={<Plus size={18} />}
                    size='sm'
                    variant='inputControl'
                    borderRadius='full'
                    isDisabled={disabled}
                  />
                  <MenuList minW='240px'>
                    {documentUploadEnabled && (
                      <MenuItem
                        icon={<Paperclip size={16} />}
                        fontSize='sm'
                        closeOnSelect
                        isDisabled={isUploading}
                        onClick={handleAttachClick}
                      >
                        {t('chat.addFiles')}
                      </MenuItem>
                    )}
                    {knowledgeToggleCommand && toggleableKnowledgeSites.map(site => (
                      <MenuItem
                        key={site.key}
                        icon={<Library size={16} />}
                        fontSize='sm'
                        closeOnSelect={false}
                        onClick={() => toggleKnowledgeSite(site.key)}
                      >
                        <Flex w='100%' alignItems='center' justifyContent='space-between' gap={3}>
                          <Text fontSize='sm'>{site.name}</Text>
                          <Switch
                            isChecked={!disabledKnowledgeSources.has(site.key)}
                            colorScheme='brand'
                            size='sm'
                            pointerEvents='none'
                          />
                        </Flex>
                      </MenuItem>
                    ))}
                    {showFilter && (
                      <>
                        <MenuDivider />
                        {/* Group header carries an info tooltip explaining what the filter does. */}
                        <Flex px={3} py={1} alignItems='center' gap={1}>
                          <Text fontSize='xs' fontWeight='semibold' color={mutedTextColor} textTransform='uppercase'>
                            {t('chat.filters')}
                          </Text>
                          <Tooltip label={t('chat.filtersInfo')} hasArrow placement='top' fontSize='xs'>
                            <Box as='span' display='inline-flex' color={mutedTextColor} cursor='help'>
                              <Info size={13} />
                            </Box>
                          </Tooltip>
                        </Flex>
                        <MenuOptionGroup
                          type='checkbox'
                          value={filterSelection}
                          onChange={value =>
                            handleFilterSelectionChange(Array.isArray(value) ? value : [value])
                          }
                        >
                          {filterOptions.map(option => (
                            <MenuItemOption key={option.value} value={option.value} fontSize='sm'>
                              <FilterOptionLabel option={option} />
                            </MenuItemOption>
                          ))}
                        </MenuOptionGroup>
                      </>
                    )}
                  </MenuList>
                </Menu>
              ) : (
                <Box />
              )}

              <Flex alignItems='center' gap={1}>
                {showModelPicker && (
                  <Menu placement='top-end'>
                    <MenuButton
                      as={Button}
                      size='xs'
                      variant='inputControl'
                      rightIcon={<ChevronDown size={12} />}
                      fontWeight='normal'
                      fontSize='xs'
                      px={2}
                      h='28px'
                    >
                      {selectedModelName || t('profile.models')}
                    </MenuButton>
                    <MenuList minW='200px' maxW='280px'>
                      {models?.map(model => {
                        const isSelected = model.id === selectedModelId
                        return (
                          <MenuItem
                            key={model.id}
                            onClick={() => onSelectModel!(model.id)}
                            bg={isSelected ? 'buttonActive' : undefined}
                            color={isSelected ? 'brand.500' : undefined}
                            fontWeight={isSelected ? 'medium' : 'normal'}
                            fontSize='sm'
                          >
                            {model.display_name || model.name}
                          </MenuItem>
                        )
                      })}
                      <MenuDivider />
                      <MenuItem
                        icon={<Settings size={13} />}
                        fontSize='xs'
                        onClick={() => navigate('/settings?tab=models')}
                      >
                        {t('profile.modelSettings')}
                      </MenuItem>
                    </MenuList>
                  </Menu>
                )}

                {/* While a response streams, the send button is just grayed
                    out (no stop yet; DVG-222 will wire real cancellation). */}
                <IconButton
                  aria-label={t('common.send')}
                  icon={<Send size={16} />}
                  size='sm'
                  colorScheme='brand'
                  borderRadius='lg'
                  isDisabled={isStreaming || sendQuestionDisabled}
                  onClick={handleSend}
                />
              </Flex>
            </Flex>
          </Box>
        </PopoverAnchor>

        <Portal>
          <PopoverContent bg={popoverBg} maxW='400px' boxShadow='xl' borderColor={borderColor}>
            <PopoverBody p={1} maxH='320px' overflowY='auto'>
              {activeCommand ? (
                isMultiSelect ? (
                  // Secondary panel: checkbox multi-select, confirmed as one union command.
                  <CommandMultiSelectList
                    command={activeCommand}
                    options={visibleOptions}
                    filter={optionFilter}
                    mutedTextColor={mutedTextColor}
                    confirmBg={popoverBg}
                    selected={multiSelected}
                    highlightedIndex={highlightedIndex}
                    onHighlight={setHighlightedIndex}
                    onBack={handleBackToCommands}
                    onToggle={toggleMultiSelectOption}
                    onConfirm={confirmMultiSelect}
                  />
                ) : (
                  // Secondary list: the active command's selectable options (entities).
                  <CommandOptionList
                    command={activeCommand}
                    mutedTextColor={mutedTextColor}
                    highlightedIndex={highlightedIndex}
                    onHighlight={setHighlightedIndex}
                    onBack={handleBackToCommands}
                    onSelectOption={applyCommandOption}
                  />
                )
              ) : (
                // Primary list: the available commands, filtered by what the user typed.
                <CommandList
                  commands={filteredCommands}
                  mutedTextColor={mutedTextColor}
                  highlightedIndex={highlightedIndex}
                  onHighlight={setHighlightedIndex}
                  onSelect={handleCommandSelect}
                />
              )}
            </PopoverBody>
          </PopoverContent>
        </Portal>
      </Popover>

      {/* Hidden file input */}
      {documentUploadEnabled && (
        <input
          ref={fileInputRef}
          type='file'
          accept='.pdf,.docx,.xlsx,.xls,.csv,.pptx,.txt'
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      )}
    </VStack>
  )
}

interface CommandListProps {
  commands: CommandDescriptor[]
  mutedTextColor: string
  highlightedIndex: number
  onHighlight: (index: number) => void
  onSelect: (command: CommandDescriptor) => void
}

/** Primary popup list: one row per available command (name + grayed description). */
const CommandList: React.FC<CommandListProps> = ({ commands, mutedTextColor, highlightedIndex, onHighlight, onSelect }) => (
  <VStack align='stretch' spacing={0}>
    {commands.map((command, index) => (
      <CommandRow
        key={command.name}
        primary={`/${command.name}`}
        secondary={command.description}
        mutedTextColor={mutedTextColor}
        isHighlighted={index === highlightedIndex}
        onMouseEnter={() => onHighlight(index)}
        onClick={() => onSelect(command)}
      />
    ))}
  </VStack>
)

interface CommandOptionListProps {
  command: CommandDescriptor
  mutedTextColor: string
  highlightedIndex: number
  onHighlight: (index: number) => void
  onBack: () => void
  onSelectOption: (command: CommandDescriptor, optionValue: string) => void
}

/**
 * Secondary popup list: the selectable options of an arg-taking command.
 * PR5 generalizes this into a multi-select; see the report notes.
 *
 * Row index 0 is the back affordance; options follow at index `i + 1`, matching
 * the navigationItems list the keyboard handler walks.
 */
const CommandOptionList: React.FC<CommandOptionListProps> = ({
  command,
  mutedTextColor,
  highlightedIndex,
  onHighlight,
  onBack,
  onSelectOption,
}) => (
  <VStack align='stretch' spacing={0}>
    <CommandRow
      primary={command.name}
      secondary=''
      mutedTextColor={mutedTextColor}
      icon={<ChevronLeft size={14} />}
      isHighlighted={highlightedIndex === 0}
      onMouseEnter={() => onHighlight(0)}
      onClick={onBack}
    />
    {command.options.map((option, index) => (
      <CommandRow
        key={option.value}
        primary={option.name}
        secondary={option.description}
        mutedTextColor={mutedTextColor}
        isHighlighted={highlightedIndex === index + 1}
        onMouseEnter={() => onHighlight(index + 1)}
        onClick={() => onSelectOption(command, option.value)}
      />
    ))}
  </VStack>
)

interface CommandMultiSelectListProps {
  command: CommandDescriptor
  /** Options in display order: selected picks pinned on top, then filter matches. */
  options: CommandOption[]
  /** The typed search filter narrowing the unselected options. */
  filter: string
  mutedTextColor: string
  /** Popup background, so the sticky confirm bar covers rows scrolling under it. */
  confirmBg: string
  selected: string[]
  highlightedIndex: number
  onHighlight: (index: number) => void
  onBack: () => void
  onToggle: (optionValue: string) => void
  onConfirm: () => void
}

/**
 * Secondary popup panel: a checkbox multi-select over an arg-taking command's
 * options. Picks (`selected`), the typed `filter` and the toggle/confirm
 * handlers are lifted to the parent so the keyboard map (typing filters, Tab
 * toggles the highlighted row and advances, Enter hot-accepts the highlighted
 * row and confirms, Space confirms the picks or aborts when there are none)
 * and the row clicks share one source of truth. Generic — works for any
 * command's `options`.
 *
 * The Confirm button and the search-bar row sit sticky at the top so they stay
 * visible while a long entity list scrolls. Neither is part of the
 * keyboard-navigable list, and neither is the back affordance (mouse-only;
 * keyboard aborts with Space) — the highlight walks the option rows only, at
 * their display index.
 */
const CommandMultiSelectList: React.FC<CommandMultiSelectListProps> = ({
  command,
  options,
  filter,
  mutedTextColor,
  confirmBg,
  selected,
  highlightedIndex,
  onHighlight,
  onBack,
  onToggle,
  onConfirm,
}) => {
  const { t } = useTranslation()

  return (
    <VStack align='stretch' spacing={0}>
      <Box px={2} py={2} position='sticky' top={0} zIndex={1} bg={confirmBg}>
        <Button
          size='sm'
          width='100%'
          colorScheme='brand'
          leftIcon={<Check size={14} />}
          isDisabled={selected.length === 0}
          onClick={onConfirm}
        >
          {t('common.confirm')}
        </Button>
        {/* Search bar fed by intercepted keystrokes; muted hint when empty. */}
        <Flex alignItems='center' gap={2} px={1} pt={2}>
          <Search size={14} />
          <Text fontSize='sm' color={filter ? undefined : mutedTextColor} noOfLines={1}>
            {filter || t('common.typeToFilter')}
          </Text>
        </Flex>
      </Box>
      <CommandRow
        primary={command.name}
        secondary=''
        mutedTextColor={mutedTextColor}
        icon={<ChevronLeft size={14} />}
        onClick={onBack}
      />
      {options.map((option, index) => {
        const isChecked = selected.includes(option.value)
        return (
          <CommandRow
            key={option.value}
            primary={option.name}
            secondary={option.description}
            mutedTextColor={mutedTextColor}
            icon={isChecked ? <SquareCheck size={16} /> : <Square size={16} />}
            isHighlighted={highlightedIndex === index}
            onMouseEnter={() => onHighlight(index)}
            onClick={() => onToggle(option.value)}
          />
        )
      })}
    </VStack>
  )
}

interface CommandRowProps {
  primary: string
  secondary: string
  mutedTextColor: string
  icon?: React.ReactNode
  isHighlighted?: boolean
  onMouseEnter?: () => void
  onClick: () => void
}

/** A single selectable row: bold primary label + grayed secondary description. */
const CommandRow: React.FC<CommandRowProps> = ({ primary, secondary, mutedTextColor, icon, isHighlighted, onMouseEnter, onClick }) => (
  <Flex
    as='button'
    type='button'
    width='100%'
    textAlign='left'
    alignItems='center'
    gap={2}
    px={3}
    py={2}
    borderRadius='md'
    bg={isHighlighted ? 'buttonHover' : undefined}
    _hover={{ bg: 'buttonHover' }}
    onMouseEnter={onMouseEnter}
    onClick={onClick}
  >
    {icon}
    <Box overflow='hidden'>
      <Text fontSize='sm' fontWeight='medium' noOfLines={1}>
        {primary}
      </Text>
      {secondary && (
        <Text fontSize='xs' color={mutedTextColor} noOfLines={1}>
          {secondary}
        </Text>
      )}
    </Box>
  </Flex>
)
